import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import {
  SectionHeading,
  Subheading as SubheadingBase
} from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import { ContentWithPaddingXl as ContentBase } from "components/misc/Layouts.js";
import { ReactComponent as CheckboxIcon } from "images/checkbox-circle.svg";
import { ReactComponent as NoteIcon } from "images/custom/Note.svg";
import CameraIcon from "images/custom/camera.png";
import BackgroundImage from "images/custom/pricing-background.png";
import { HASH_PRICING, HASH_CONTACT } from "helpers/Constants";

const Container = styled.div`
  ${tw`relative -mx-8 px-8`}
  ${css`
    background-image: url(${BackgroundImage}),
      linear-gradient(180deg, #00a5e6 37.5%, #ffffff 100%);
    background-repeat: repeat;
  `}
  img {
    ${tw`w-80 h-80 mx-auto object-contain`}
  }
`;

const ContentWithPaddingXl = tw(
  ContentBase
)`relative z-10 mx-auto px-0 py-10 sm:px-6 md:px-8 lg:px-12 xl:px-24 sm:py-20 flex flex-col max-w-screen-xl`;
const HeaderContainer = tw.div`mt-10 w-full flex flex-col items-center`;
const Subheading = tw(SubheadingBase)`mb-4 text-gray-100`;
const Heading = tw(
  SectionHeading
)`w-full text-4xl lg:text-5xl text-white font-bold`;
const Description = tw(
  SectionDescription
)`w-full text-xl lg:text-2xl text-white text-center max-w-full`;

const PlansContainer = tw.div`mt-16 flex flex-col items-center lg:flex-row lg:items-stretch lg:justify-center text-gray-900 font-medium`;
const Plan = styled.div`
  ${tw`w-full max-w-xs bg-white rounded-lg shadow-sm py-10 px-6 sm:px-10 lg:px-6 lg:py-10 xl:py-10 xl:px-8 mx-3 flex flex-col justify-between mt-16 first:mt-0 lg:mt-0 shadow-raised`}
`;

const PlanHeader = styled.div`
  .nameAndFeaturedContainer {
    ${tw`flex flex-wrap flex-col sm:flex-row justify-between items-center`}
  }
  .name {
    ${tw`text-2xl text-skywatch-gray1 font-bold uppercase tracking-wider mr-3`}
  }
  .featuredText {
    ${tw`text-xs font-bold px-3 rounded py-2 uppercase bg-green-300 text-green-900 leading-none mt-4 sm:mt-0 w-full sm:w-auto text-center`}
  }
  .pricingContainer {
    ${tw`mt-6 flex items-end justify-between`}
    .currentPrice {
      ${tw`text-lg text-skywatch-gray3 font-bold leading-none`}
      .bigText {
        ${tw`text-4xl text-skywatch-gray1 font-bold`}
      }
    }
    .oldPrice {
      ${tw`text-lg text-skywatch-gray3`}
    }
  }
  .description {
    ${tw`mt-8 font-normal text-skywatch-gray1 text-base`}
  }
`;
const PlanFeatures = styled.ul`
  ${tw`mt-10 flex-1 border-t lg:-mx-6 -mx-6 sm:-mx-10 py-10 px-6 sm:px-10 lg:p-6 xl:-mx-8 xl:p-10`}
  .feature {
    ${tw`flex items-start mt-6 first:mt-0`}
    .icon {
      ${tw`w-6 h-6 text-skywatch-primary500 flex-shrink-0`}
    }
    .text {
      ${tw`font-semibold text-skywatch-gray1 text-xl tracking-wide ml-3`}
    }
    .note {
      ${tw`font-normal text-skywatch-gray2 text-base tracking-wide ml-3`}
    }
  }
`;

const PlanAction = tw.div`mt-4`;
const ActionButton = styled(PrimaryButtonBase)`
  ${tw`block text-center text-lg font-semibold tracking-wider w-full text-gray-100 hocus:bg-skywatch-primary500 bg-skywatch-primary500 px-6 py-4 rounded hover:bg-skywatch-primary500 focus:shadow-none focus:outline-none transition-colors duration-300`}
`;

export default ({
  subheading = "",
  heading = "年繳用戶獨享月費減免 2 個月",
  description1 = "雲端攝影機 NT$4,190 元起",
  description2 = "歡迎舊會員回娘家洽詢專屬優惠",
  plans = null,
  primaryButtonText = "聯絡我們"
}) => {
  const defaultPlans = [
    {
      name: "HD 畫質",
      price: ["$1,090", "/年"],
      oldPrice: "$109 / 月",
      description: "以每天不到 3 元的超低優惠價享有 720P 高畫質雲端儲存方案。",
      features: ["影像畫質 720P", "智慧日誌", "智慧縮時", "雲端 3 天循環錄影"],
      note: "另可升級 7/15/30 天循環錄影，詳情請洽銷售人員。",
      url: HASH_CONTACT
    },
    {
      name: "Full HD 畫質",
      price: ["$1,490", "/年"],
      oldPrice: "$149 / 月",
      description:
        "以每天不到 4.5 元的超低優惠價享有 1080P 超高畫質雲端儲存方案。",
      features: ["影像畫質 1080P", "智慧日誌", "智慧縮時", "雲端 3 天循環錄影"],
      note: "另可升級 7/15/30 天循環錄影，詳情請洽銷售人員。",
      url: HASH_CONTACT
    }
  ];

  if (!plans) plans = defaultPlans;

  return (
    <Container id={HASH_PRICING}>
      <ContentWithPaddingXl>
        <img src={CameraIcon} alt="camera" />
        <HeaderContainer>
          {subheading && <Subheading>{subheading}</Subheading>}
          <Heading>{heading}</Heading>
          {description1 && description2 && (
            <Description>
              {description1}
              <br />
              {description2}
            </Description>
          )}
        </HeaderContainer>
        <PlansContainer>
          {plans.map((plan, index) => (
            <Plan key={index} featured={plan.featured}>
              <PlanHeader>
                <span className="nameAndFeaturedContainer">
                  <span className="name">{plan.name}</span>
                  {plan.featured && (
                    <span className="featuredText">{plan.featured}</span>
                  )}
                </span>
                <div className="pricingContainer">
                  <span className="currentPrice">
                    <span className="bigText">{plan.price[0]}</span>
                    {plan.price[1]}{" "}
                  </span>
                  {plan.oldPrice && (
                    <span className="oldPrice">{plan.oldPrice}</span>
                  )}
                </div>
                <p className="description">{plan.description}</p>
              </PlanHeader>
              <PlanFeatures>
                {plan.features.map((feature, index) => (
                  <li className="feature" key={index}>
                    <CheckboxIcon className="icon" />
                    <span className="text">{feature}</span>
                  </li>
                ))}
                <li className="feature"></li>
                <li className="feature">
                  <NoteIcon className="icon" />
                  <span className="note">{plan.note}</span>
                </li>
              </PlanFeatures>
              <PlanAction>
                <ActionButton as="a" href={plan.url}>
                  {primaryButtonText}
                </ActionButton>
              </PlanAction>
            </Plan>
          ))}
        </PlansContainer>
      </ContentWithPaddingXl>
    </Container>
  );
};
